/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * DATA ACCESS LAYER : Staff Video Content
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Ce fichier gère la récupération des vidéos pour les membres du staff
 * en fonction des clients qui leur sont assignés.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { supabase } from "@/integrations/supabase/client";

export interface StaffVideoTask {
  id: string;
  title: string | null;
  status: string | null;
  content_type: string | null;
  thumbnail_url: string | null;
  video_1_thumbnail: string | null;
  shooting_id: string | null;
  created_at: string;
  last_feedback_at: string | null;
  // Client info
  client_id: string;
  client_first_name: string | null;
  client_last_name: string | null;
  client_avatar_url: string | null;
  // Publication info
  earliest_publication_date: string | null;
  channels: string[];
  // Deadline info (persisted in DB, auto-calculated by trigger)
  derush_deadline: string | null;
  // Virality
  virality_predicted: string | null;
  // Monteur-specific
  is_multihook: boolean | null;
  video_1_url: string | null;
  video_2_url: string | null;
  video_3_url: string | null;
  editing_deadline: string | null;
  delivery_deadline: string | null;
  // VE info (for monteur view)
  ve_first_name: string | null;
  ve_last_name: string | null;
  ve_avatar_url: string | null;
  // Publications detail (for review dialog)
  publications: Array<{ channel: string; scheduled_at: string | null }>;
}

/**
 * Soustrait N jours ouvrés d'une date (pour calcul de deadlines monteur)
 */
function subtractBusinessDays(dateStr: string, days: number): string {
  const date = new Date(dateStr);
  let remaining = days;
  while (remaining > 0) {
    date.setDate(date.getDate() - 1);
    const day = date.getDay();
    if (day !== 0 && day !== 6) remaining--;
  }
  date.setHours(23, 59, 59, 999);
  return date.toISOString();
}

/**
 * Récupère les vidéos des clients assignés au Virality Expert
 * Le VE voit les vidéos en statut: shot, derush_pending, derush_done
 */
export async function getVideosForViralityExpert(userId: string): Promise<{
  success: boolean;
  data: StaffVideoTask[];
  error?: unknown;
}> {
  try {
    // 1. Récupérer les clients assignés à ce VE
    const { data: clients, error: clientsError } = await supabase
      .from("users")
      .select("id, first_name, last_name, avatar_url")
      .eq("video_editor_id", userId);

    if (clientsError) throw clientsError;
    if (!clients || clients.length === 0) {
      return { success: true, data: [] };
    }

    const clientIds = clients.map(c => c.id);
    const clientMap = new Map(clients.map(c => [c.id, c]));

    // 2. Récupérer les vidéos de ces clients
    // VE statuts: shot -> derush_done (dérush) + editing_review (validation interne) + redelivery_review (relivraison)
    const veStatuses = ['shot', 'derush_pending', 'derush_done', 'editing_review', 'redelivery_review'];
    
    const { data: videos, error: videosError } = await supabase
      .from("video_content")
      .select(`
        id,
        title,
        status,
        content_type,
        virality_predicted,
        thumbnail_url,
        video_1_thumbnail,
        shooting_id,
        created_at,
        last_feedback_at,
        user_id,
        is_multihook,
        video_1_url,
        video_2_url,
        video_3_url,
        video_content_publications (
          channel,
          scheduled_at,
          derush_deadline
        )
      `)
      .in("user_id", clientIds)
      .in("status", veStatuses)
      .order("created_at", { ascending: false });

    if (videosError) throw videosError;

    // 3. Transformer les données
    const tasks: StaffVideoTask[] = (videos || []).map((video: any) => {
      const client = clientMap.get(video.user_id);
      const publications = video.video_content_publications || [];
      
      // Trouver la date de publication la plus proche + deadline dérush
      let earliestDate: string | null = null;
      let earliestDerushDeadline: string | null = null;
      const channels: string[] = [];
      
      publications.forEach((pub: any) => {
        if (pub.channel && !channels.includes(pub.channel)) {
          channels.push(pub.channel);
        }
        if (pub.scheduled_at) {
          if (!earliestDate || pub.scheduled_at < earliestDate) {
            earliestDate = pub.scheduled_at;
            earliestDerushDeadline = pub.derush_deadline || null;
          }
        }
      });

      return {
        id: video.id,
        title: video.title,
        status: video.status,
        content_type: video.content_type || null,
        thumbnail_url: video.thumbnail_url,
        video_1_thumbnail: video.video_1_thumbnail,
        shooting_id: video.shooting_id,
        created_at: video.created_at,
        last_feedback_at: video.last_feedback_at,
        client_id: video.user_id,
        client_first_name: client?.first_name || null,
        client_last_name: client?.last_name || null,
        client_avatar_url: client?.avatar_url || null,
        earliest_publication_date: earliestDate,
        channels,
        derush_deadline: earliestDerushDeadline,
        virality_predicted: video.virality_predicted || null,
        is_multihook: video.is_multihook || null,
        video_1_url: video.video_1_url || null,
        video_2_url: video.video_2_url || null,
        video_3_url: video.video_3_url || null,
        editing_deadline: null,
        delivery_deadline: null,
        ve_first_name: null,
        ve_last_name: null,
        ve_avatar_url: null,
        publications: publications.map((pub: any) => ({ channel: pub.channel, scheduled_at: pub.scheduled_at })),
      };
    });

    return { success: true, data: tasks };
  } catch (error) {
    console.error("Error fetching videos for virality expert:", error);
    return { success: false, data: [], error };
  }
}

/**
 * Récupère les vidéos des clients assignés au Monteur
 * Le Monteur voit les vidéos en statut: editing_pending, editing_review
 */
export async function getVideosForMonteur(userId: string): Promise<{
  success: boolean;
  data: StaffVideoTask[];
  error?: unknown;
}> {
  try {
    // 1. Récupérer les clients assignés à ce Monteur
    const { data: clients, error: clientsError } = await supabase
      .from("users")
      .select("id, first_name, last_name, avatar_url, video_editor_id")
      .eq("monteur_id", userId);

    if (clientsError) throw clientsError;
    if (!clients || clients.length === 0) {
      return { success: true, data: [] };
    }

    const clientIds = clients.map(c => c.id);
    const clientMap = new Map(clients.map(c => [c.id, c]));

    // Fetch VE info for each client
    const veIds = [...new Set(clients.map((c: any) => c.video_editor_id).filter(Boolean))];
    const veMap = new Map<string, { first_name: string | null; last_name: string | null; avatar_url: string | null }>();
    if (veIds.length > 0) {
      const { data: veUsers } = await supabase
        .from("users")
        .select("id, first_name, last_name, avatar_url")
        .in("id", veIds);
      veUsers?.forEach((ve: any) => veMap.set(ve.id, ve));
    }

    // 2. Récupérer les vidéos de ces clients (statuts Monteur)
    const monteurStatuses = ['editing_pending', 'editing_review', 'client_feedback'];
    
    const { data: videos, error: videosError } = await supabase
      .from("video_content")
      .select(`
        id,
        title,
        status,
        content_type,
        virality_predicted,
        thumbnail_url,
        video_1_thumbnail,
        shooting_id,
        created_at,
        last_feedback_at,
        user_id,
        is_multihook,
        video_1_url,
        video_2_url,
        video_3_url,
        video_content_publications (
          channel,
          scheduled_at
        )
      `)
      .in("user_id", clientIds)
      .in("status", monteurStatuses)
      .order("created_at", { ascending: false });

    if (videosError) throw videosError;

    // 3. Transformer les données
    const tasks: StaffVideoTask[] = (videos || []).map((video: any) => {
      const client = clientMap.get(video.user_id) as any;
      const publications = video.video_content_publications || [];
      
      let earliestDate: string | null = null;
      const channels: string[] = [];
      
      publications.forEach((pub: any) => {
        if (pub.channel && !channels.includes(pub.channel)) {
          channels.push(pub.channel);
        }
        if (pub.scheduled_at) {
          if (!earliestDate || pub.scheduled_at < earliestDate) {
            earliestDate = pub.scheduled_at;
          }
        }
      });

      // Calcul des deadlines monteur depuis la date de publication
      const editingDeadline = earliestDate ? subtractBusinessDays(earliestDate, 14) : null;
      const deliveryDeadline = earliestDate ? subtractBusinessDays(earliestDate, 7) : null;

      // VE info
      const ve = client?.video_editor_id ? veMap.get(client.video_editor_id) : null;

      return {
        id: video.id,
        title: video.title,
        status: video.status,
        content_type: video.content_type || null,
        thumbnail_url: video.thumbnail_url,
        video_1_thumbnail: video.video_1_thumbnail,
        shooting_id: video.shooting_id,
        created_at: video.created_at,
        last_feedback_at: video.last_feedback_at,
        client_id: video.user_id,
        client_first_name: client?.first_name || null,
        client_last_name: client?.last_name || null,
        client_avatar_url: client?.avatar_url || null,
        earliest_publication_date: earliestDate,
        channels,
        derush_deadline: null,
        virality_predicted: video.virality_predicted || null,
        is_multihook: video.is_multihook || null,
        video_1_url: video.video_1_url || null,
        video_2_url: video.video_2_url || null,
        video_3_url: video.video_3_url || null,
        editing_deadline: editingDeadline,
        delivery_deadline: deliveryDeadline,
        ve_first_name: ve?.first_name || null,
        ve_last_name: ve?.last_name || null,
        ve_avatar_url: ve?.avatar_url || null,
        publications: publications.map((pub: any) => ({ channel: pub.channel, scheduled_at: pub.scheduled_at })),
      };
    });

    return { success: true, data: tasks };
  } catch (error) {
    console.error("Error fetching videos for monteur:", error);
    return { success: false, data: [], error };
  }
}
